import React, { useEffect, useMemo, useState } from "react";

import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import EnhancedTable from "../table/Table";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchCSGOs } from "../../redux/actions/CSGOActions";
import { fetchUsers } from "../../redux/actions/UserActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import { CSGOModel } from "../../models/CSGOModel";
import Loader from "../../components/loader/Loader";
import CsgoForm from "./CsgoForm";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  name: string;
  description: string;
  csgoUser: string;
  createdDate: string;
  // status: string;
};

const CsgoPage = () => {
  const dispatch = useAppDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<CSGOModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allCSGOs = useAppSelector((state) => state.csgo.all_CSGOs);
  const allUsers = useAppSelector((state) => state.user.all_Users);

  const isCsgoFetching = useAppSelector((state) => state.csgo.isCsgoFetching);

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
      },
      {
        id: "csgoUser",
        numeric: false,
        disablePadding: false,
        label: "User",
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  //To fetch all CSGOs and Users
  useEffect(() => {
    dispatch(fetchCSGOs());
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    setRootData(() =>
      allCSGOs.map((csgo) => {
        const user = allUsers.find((user) => user.userId === csgo.csgouserId);
        const userName = user ? `${user.firstName} ${user.lastName}` : "";
        return {
          id: csgo.csgoid,
          name: csgo.name,
          description: csgo.description,
          csgoUser: userName,
          createdDate: displayDateFormart(csgo.createdDate),
          // status: csgo.isActive ? "Active" : "Inactive",
        };
      })
    );
  }, [allCSGOs, allUsers]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editCSGO = allCSGOs.find((rec) => rec.csgoid === id);
    setMode("edit");
    setEditData(editCSGO);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allCSGOs) && isCsgoFetching ? <Loader /> : null}
      <PageTitle title="CSGOs" btnText="Add CSGO" onClick={createHandler} />
      <PageSearch placeholder="Search CSGOs" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={mode === "add" ? "Create CSGO" : "Edit CSGO"}
          handleModalClose={handleModalClose}
        >
          <CsgoForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default CsgoPage;
